import { getApiHeaders } from '../utils/apiHeaders';
import { VendorQuoteMessage, VendorQuoteSendPayload } from '../types/VendorQuote';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const parseReferences = (value: any): string[] | undefined => {
  if (Array.isArray(value)) {
    const refs = value.map((item) => String(item).trim()).filter(Boolean);
    return refs.length ? refs : undefined;
  }
  if (typeof value === 'string') {
    const refs = value
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    return refs.length ? refs : undefined;
  }
  return undefined;
};

const coerceMessage = (m: any, fallbackId: number): VendorQuoteMessage => ({
  id: Number(m?.id || m?.email_id || m?.message_id || fallbackId),
  direction: m?.direction || m?.type || m?.kind || (m?.inbound ? 'inbound' : m?.outbound ? 'outbound' : 'outbound'),
  subject: m?.subject || m?.title || '',
  bodyText: m?.bodyText || m?.body_text || m?.body || m?.text || '',
  bodyHtml: m?.bodyHtml || m?.body_html || '',
  toEmail: m?.toEmail || m?.to_email || m?.to || '',
  fromEmail: m?.fromEmail || m?.from_email || m?.from || '',
  vendorEmail: m?.vendorEmail || m?.vendor_email || m?.supplierEmail || m?.supplier_email || '',
  vendorName: m?.vendorName || m?.vendor_name || m?.supplierName || m?.supplier_name || '',
  createdAt: m?.createdAt || m?.created_at || m?.sentAt || m?.sent_at || m?.date || '',
  status: m?.status || m?.state || '',
  threadId: m?.threadId || m?.thread_id || m?.conversationId || m?.conversation_id || m?.rfqId || m?.rfq_id || undefined,
  parentId: m?.parentId || m?.parent_id || m?.replyToId || m?.reply_to_id || m?.parent_email_id || undefined,
  parentMessageId: m?.parentMessageId || m?.parent_message_id || m?.replyToMessageId || m?.reply_to_message_id || undefined,
  messageId: m?.messageId || m?.message_id || m?.internetMessageId || m?.internet_message_id || m?.emailMessageId || undefined,
  inReplyTo: m?.inReplyTo || m?.in_reply_to || undefined,
  references: parseReferences(m?.references || m?.email_references || m?.referenceIds || m?.reference_ids),
  attachments: Array.isArray(m?.attachments) ? m.attachments : undefined,
});

const normalizeMessages = (data: any): VendorQuoteMessage[] => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.messages)
          ? data.messages
          : Array.isArray(data?.thread)
            ? data.thread
            : Array.isArray(data?.rfqs)
              ? data.rfqs
              : [];

  const out: VendorQuoteMessage[] = [];
  list.forEach((item: any, idx: number) => {
    const itemContext = {
      threadId: item?.threadId || item?.thread_id || item?.conversationId || item?.conversation_id || item?.rfqId || item?.rfq_id || item?.id,
      subject: item?.subject || item?.title || '',
      parentId: item?.parentId || item?.parent_id,
    };

    if (Array.isArray(item?.emails)) {
      item.emails.forEach((em: any, eIdx: number) => {
        out.push(coerceMessage({ ...itemContext, ...em, subject: em?.subject || itemContext.subject || '' }, idx * 1000 + eIdx));
      });
      return;
    }
    if (Array.isArray(item?.messages)) {
      item.messages.forEach((em: any, eIdx: number) => {
        out.push(coerceMessage({ ...itemContext, ...em, subject: em?.subject || itemContext.subject || '' }, idx * 1000 + eIdx));
      });
      return;
    }
    out.push(coerceMessage({ ...itemContext, ...item }, idx));
  });
  return out;
};

export const fetchVendorQuoteThread = async (queryId: number): Promise<VendorQuoteMessage[]> => {
  const res = await fetch(`${API_BASE_URL}/api/queries/${queryId}/vendor-quotes`, {
    headers: getApiHeaders('json'),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch vendor quote thread: ${res.statusText}`);
  }
  const data = await res.json();
  return normalizeMessages(data);
};

export const sendVendorQuote = async (queryId: number, payload: VendorQuoteSendPayload) => {
  const res = await fetch(`${API_BASE_URL}/api/queries/${queryId}/vendor-quotes/send`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to send vendor quote: ${errorText}`);
  }
  return res.json();
};
